import React, { useCallback, useEffect, useState } from 'react';
import { Calendar } from 'components/Calendar';
import { Views } from 'react-big-calendar';
import { makeStyles } from '@material-ui/core/styles';
import { CALENDAR_UI } from 'utils/constants/css';
import { useDispatch, useSelector } from 'react-redux';
import {
  calendarActions,
  calendarSelectors,
} from 'containers/Pages/CalendarUI';
import { endOfMonth, startOfMonth } from 'utils/time';
import { severityRatingEnums } from 'containers/Pages/Events/Layouts';
import cn from 'classnames';
import CustomToolbar from './customToolbar';
import Proptypes from 'prop-types';

const useStyles = makeStyles(theme => ({
  standardText: {
    backgroundColor: theme.palette.severity.notRated,
  },
  veryHighText: {
    backgroundColor: theme.palette.error.main,
  },
  highText: {
    backgroundColor: theme.palette.warning.main,
  },
  lowText: {
    backgroundColor: theme.palette.success.main,
  },
  medium: {
    backgroundColor: 'lightGreen',
  },
  veryLowText: {
    backgroundColor: theme.palette.success.highlight,
  },
  calendar: {
    height: CALENDAR_UI.height,
    padding: CALENDAR_UI.padding,
    maxWidth: CALENDAR_UI.maxWidth,
    background: theme.palette.background.paper,
    '& .rbc-off-range-bg': {
      backgroundColor: theme.palette.grey[500],
      color: theme.palette.text.primary,
    },
    '& .rbc-off-range': {
      color: theme.palette.text.primary,
    },
    '& .rbc-time-view': {},
    '& .rbc-today': {
      backgroundColor: theme.palette.primary.light,
    },
    '& .rbc-btn-group': {
      '& button:nth-of-type(n)': {
        // select all buttons inside this class
        color: theme.palette.text.primary,
        border: 'none',
        background: theme.palette.primary.main,
        cursor: 'pointer',
        marginRight: '5px',
        borderRadius: '0',
      },
    },
  },
}));

const EventWrapperComponent = ({ event, children }) => {
  const classes = useStyles();
  const newChildren = { ...children };
  const newChildrenProps = { ...newChildren.props };
  switch (event.severityRating) {
    case severityRatingEnums.veryHigh:
      newChildrenProps.className = cn(
        newChildrenProps.className,
        classes.veryHighText,
      );

      break;
    case severityRatingEnums.high:
      newChildrenProps.className = cn(
        newChildrenProps.className,
        classes.highText,
      );
      break;
    case severityRatingEnums.medium:
      newChildrenProps.className = cn(
        newChildrenProps.className,
        classes.medium,
      );
      break;
    case severityRatingEnums.low:
      newChildrenProps.className = cn(
        newChildrenProps.className,
        classes.lowText,
      );
      break;
    case severityRatingEnums.veryLow:
      newChildrenProps.className = cn(
        newChildrenProps.className,
        classes.veryLowText,
      );
      break;

    default:
      newChildrenProps.className = cn(
        newChildrenProps.className,
        classes.standardText,
      );

      break;
  }

  newChildren.props = { ...newChildrenProps };

  return <div>{newChildren}</div>;
};
EventWrapperComponent.propTypes = {
  event: Proptypes.object,
  children: Proptypes.node,
};
const CalendarUI = () => {
  const [toFetchDate, setToFetchDate] = useState(new Date());
  // const [view, setView] = useState(Views.MONTH);
  //actions
  const dispatch = useDispatch();
  const dispatchFetchCalendarTaskEvents = useCallback(
    ({ startDate, endDate }) => {
      dispatch(calendarActions.fetchCalendarTaskEvents({ startDate, endDate }));
    },
    [dispatch],
  );
  const dispatchFetchPatrolEvents = useCallback(
    ({ startDate, endDate }) => {
      dispatch(calendarActions.fetchPatrolEvents({ startDate, endDate }));
    },
    [dispatch],
  );
  const dispatchFetchTransportEvents = useCallback(
    ({ startDate, endDate }) => {
      dispatch(calendarActions.fetchTransportEvents({ startDate, endDate }));
    },
    [dispatch],
  );
  // selectors
  const tasks = useSelector(state =>
    calendarSelectors.getTasksTransformed(state),
  );

  const patrolEvents = useSelector(state =>
    calendarSelectors.getPatrolTransformed(state),
  );
  const transportEvents = useSelector(state =>
    calendarSelectors.getTransportEventsTransformed(state),
  );

  const classes = useStyles();
  const typeOfViews = {
    [Views.MONTH]: true,
    // [Views.WORK_WEEK]: true,
    // [Views.DAY]: true,
    // [Views.WEEK]: true,
    // [Views.AGENDA]: true,
  };
  const onNavigate = props => {
    if (typeof props === 'object') {
      setToFetchDate(props);
    }
  };
  //Note: can be used in future
  // const onView = view => {
  //   // lets you know which view you are on
  //   setView(view);
  // };

  useEffect(() => {
    const today = toFetchDate || new Date();
    const startDate = startOfMonth({
      date: today,
    });

    const endDate = endOfMonth({ date: toFetchDate });

    dispatchFetchCalendarTaskEvents({ startDate, endDate });
    dispatchFetchPatrolEvents({ startDate, endDate });
    dispatchFetchTransportEvents({ startDate, endDate });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toFetchDate]);

  return (
    <Calendar
      typeOfViews={typeOfViews}
      defaultView={Views.MONTH}
      className={classes.calendar}
      // date={toFetchDate}
      onNavigate={onNavigate}
      // onView={onView}
      events={[...tasks, ...patrolEvents, ...transportEvents]}
      eventWrapper={EventWrapperComponent}
      toolBarWrapper={CustomToolbar}
    />
  );
};

export default CalendarUI;
